import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS, Icon } from 'components/Icon';
import { Txt } from 'components/Txt';
import { theme } from 'lib/theme';
import React from 'react';
import { AddTemplateAction } from 'screens/AssessmentScreen/Explorer/ExplorerActionRow';
import { AddTemplateControl } from 'screens/AssessmentScreen/Explorer/ExplorerControlRow';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { Button } from 'components/Button';
import { LayoutChangeEvent, View } from 'react-native';
import {
  ActionSuggestion,
  ChecklistItem,
  ChecklistItemHasSuggestedActions,
  ChecklistItemHasSuggestedControls,
  ChecklistItemResult,
  ChecklistItemResultEnum,
  ControlSuggestion,
  InstanceParentParams,
} from 'store/assessments/types';

function getActionSuggestionsFromChecklistItem(
  checklistItemId: number,
  checklistItemResult: ChecklistItemResultEnum | null | undefined,
  itemHasSuggestion: ChecklistItemHasSuggestedActions,
): ActionSuggestion[] {
  if (checklistItemResult === null || checklistItemResult === undefined) {
    return [];
  }
  return (
    itemHasSuggestion[checklistItemId]?.[
      checklistItemResult === ChecklistItemResultEnum.cross
        ? 'negative'
        : checklistItemResult === ChecklistItemResultEnum.tick
          ? 'positive'
          : 'na'
    ] ?? []
  );
}

function getControlSuggestionsFromChecklistItem(
  checklistItemId: number,
  checklistItemResult: ChecklistItemResultEnum | null | undefined,
  itemHasSuggestion: ChecklistItemHasSuggestedControls,
): ControlSuggestion[] {
  if (checklistItemResult === null || checklistItemResult === undefined) {
    return [];
  }
  return (
    itemHasSuggestion[checklistItemId]?.[
      checklistItemResult === ChecklistItemResultEnum.cross
        ? 'negative'
        : checklistItemResult === ChecklistItemResultEnum.tick
          ? 'positive'
          : 'na'
    ] ?? []
  );
}

export const ChecklistItemRow: React.FC<{
  checklistItem: ChecklistItem;
  checklistItemResults: Record<string, ChecklistItemResult['result'] | null>;
  canAddActions: boolean;
  canAddControls: boolean;
  suggestedActionsByChecklistItem: ChecklistItemHasSuggestedActions;
  suggestedControlsByChecklistItem: ChecklistItemHasSuggestedControls;
  suggestionsUnderChecklistItems: boolean;
  handleSaveChecklistItemResult:
    | null
    | ((
        checklistItemId: number,
        newResult: ChecklistItemResultEnum | null,
      ) => void);
  questionId: string;
  fieldInstanceId: string | null;
  parent: InstanceParentParams;
}> = ({
  checklistItem,
  checklistItemResults,
  canAddActions,
  canAddControls,
  suggestedActionsByChecklistItem,
  suggestedControlsByChecklistItem,
  suggestionsUnderChecklistItems,
  handleSaveChecklistItemResult,
  questionId,
  fieldInstanceId,
  parent,
}) => {
  const [size, setSize] = React.useState<'small' | 'large'>('large');
  const sizeIsSmall = size === 'small';

  const checklistItemResult = checklistItemResults[checklistItem.id];
  const resultIsTick = checklistItemResult === ChecklistItemResultEnum.tick;
  const resultIsCross = checklistItemResult === ChecklistItemResultEnum.cross;
  const resultIsNA = checklistItemResult === ChecklistItemResultEnum.dash;

  const actionSuggestions = canAddActions
    ? getActionSuggestionsFromChecklistItem(
        checklistItem.id,
        checklistItemResult,
        suggestedActionsByChecklistItem,
      )
    : [];
  const controlSuggestions = canAddControls
    ? getControlSuggestionsFromChecklistItem(
        checklistItem.id,
        checklistItemResult,
        suggestedControlsByChecklistItem,
      )
    : [];

  const handleLayout = React.useCallback(
    (event: LayoutChangeEvent) =>
      setSize(event.nativeEvent.layout.width < 500 ? 'small' : 'large'),
    [],
  );

  return (
    <CardRow
      key={checklistItem.id}
      style={
        handleSaveChecklistItemResult
          ? {
              backgroundColor: checklistItemResult
                ? 'white'
                : theme.colors.white50,
            }
          : undefined
      }
    >
      <View
        style={{
          flexDirection: sizeIsSmall ? 'column' : 'row',
          alignItems: sizeIsSmall ? undefined : 'center',
        }}
        onLayout={handleLayout}
      >
        <View
          style={{
            flexDirection: 'row',
            flex: 1,
            alignItems: 'center',
          }}
        >
          {!handleSaveChecklistItemResult &&
            (checklistItemResult === ChecklistItemResultEnum.tick ? (
              <Icon
                source={ICONS.CheckLight}
                color={theme.colors.green}
                style={{ marginRight: theme.padding }}
              />
            ) : checklistItemResult === ChecklistItemResultEnum.cross ? (
              <Icon
                source={ICONS.XmarkLight}
                color={theme.colors.red}
                style={{ marginRight: theme.padding }}
              />
            ) : checklistItemResult === ChecklistItemResultEnum.dash ? (
              <Icon
                source={ICONS.DashLight}
                color="#999999"
                style={{ marginRight: theme.padding }}
              />
            ) : (
              <Icon
                source={ICONS.Blank}
                color="#999999"
                style={{ marginRight: theme.padding }}
              />
            ))}
          <Txt style={{ flex: 1 }}>{checklistItem.text}</Txt>
        </View>
        {handleSaveChecklistItemResult && (
          <ViewWithSeparators
            horizontal
            style={{
              backgroundColor: '#f1f1f1',
              padding: 2,
              borderRadius: 99,
              overflow: 'hidden',
              flex: sizeIsSmall ? 1 : undefined,
              marginTop: sizeIsSmall ? theme.paddingHorizontalHalf : undefined,
              marginLeft: sizeIsSmall ? undefined : theme.padding,
            }}
            separatorColor="transparent"
          >
            <Button
              icon={ICONS.CheckLight}
              iconColor={resultIsTick ? 'white' : theme.colors.green}
              backgroundColor={
                resultIsTick ? theme.colors.green : 'transparent'
              }
              styleHovered={{
                backgroundColor: resultIsTick
                  ? theme.colors.green
                  : 'transparent',
              }}
              stylePressed={{
                backgroundColor: resultIsTick
                  ? theme.colors.green
                  : 'transparent',
              }}
              style={{ flex: sizeIsSmall ? 1 : undefined }}
              onPress={
                handleSaveChecklistItemResult
                  ? () =>
                      handleSaveChecklistItemResult(
                        checklistItem.id,
                        ChecklistItemResultEnum.tick,
                      )
                  : undefined
              }
            />
            <Button
              icon={ICONS.XmarkLight}
              iconColor={resultIsCross ? 'white' : theme.colors.red}
              backgroundColor={resultIsCross ? theme.colors.red : 'transparent'}
              styleHovered={{
                backgroundColor: resultIsCross
                  ? theme.colors.red
                  : 'transparent',
              }}
              stylePressed={{
                backgroundColor: resultIsCross
                  ? theme.colors.red
                  : 'transparent',
              }}
              style={{ flex: sizeIsSmall ? 1 : undefined }}
              onPress={
                handleSaveChecklistItemResult
                  ? () =>
                      handleSaveChecklistItemResult(
                        checklistItem.id,
                        ChecklistItemResultEnum.cross,
                      )
                  : undefined
              }
            />
            <Button
              icon={ICONS.DashLight}
              backgroundColor={resultIsNA ? '#999' : 'transparent'}
              styleHovered={{
                backgroundColor: resultIsNA ? '#999' : 'transparent',
              }}
              stylePressed={{
                backgroundColor: resultIsNA ? '#999' : 'transparent',
              }}
              style={{ flex: sizeIsSmall ? 1 : undefined }}
              iconColor={resultIsNA ? 'white' : 'black'}
              onPress={
                handleSaveChecklistItemResult
                  ? () =>
                      handleSaveChecklistItemResult(
                        checklistItem.id,
                        ChecklistItemResultEnum.dash,
                      )
                  : undefined
              }
            />
            <Button
              icon={ICONS.TrashCanLight}
              backgroundColor="transparent"
              onPress={
                handleSaveChecklistItemResult
                  ? () => handleSaveChecklistItemResult(checklistItem.id, null)
                  : undefined
              }
              style={{ flex: sizeIsSmall ? 1 : undefined }}
              disabled={!checklistItemResult}
            />
          </ViewWithSeparators>
        )}
      </View>
      {suggestionsUnderChecklistItems &&
        (!!actionSuggestions.length || !!controlSuggestions.length) && (
          <Card
            labelSize="xxsmall"
            marginHorizontal={false}
            style={{ marginTop: theme.padding }}
            bodyStyle={{
              borderColor: '#f1f1f1',
              borderWidth: 1,
            }}
          >
            <ViewWithSeparators>
              {actionSuggestions.map((action) => (
                <AddTemplateAction
                  key={action.id}
                  parentId={questionId}
                  parentType="question"
                  templateId={action.id}
                  fieldInstanceId={fieldInstanceId}
                  parent={parent}
                />
              ))}
              {controlSuggestions.map((control) => (
                <AddTemplateControl
                  key={control.id}
                  parentId={questionId}
                  parentType="question"
                  templateId={control.id}
                  fieldInstanceId={fieldInstanceId}
                  parent={parent}
                />
              ))}
            </ViewWithSeparators>
          </Card>
        )}
    </CardRow>
  );
};
